import React from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/platform", label: "Platform" },
  { to: "/use-cases", label: "Use cases" },
  { to: "/roadmap", label: "Roadmap" },
  { to: "/demo", label: "Demo" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 24));

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#120829]/70 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 h-[72px]">
        <Link to="/" data-testid="nav-home-link">
          <Logo />
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-[14px] font-semibold rounded-md transition-colors ${
                  isActive
                    ? "text-white"
                    : "text-white/75 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-4 right-4 -bottom-0.5 h-px bg-gradient-to-r from-brand-purpleLight via-brand-orange to-brand-purpleLight"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/demo"
            data-testid="nav-book-demo-btn"
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-orange text-white text-[14px] font-semibold hover:bg-[#e15d38] transition-colors shadow-[0_10px_30px_-10px_rgba(209,79,42,0.7)]"
          >
            Book demo
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
              →
            </span>
          </Link>
        </div>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-[#120829]/90 backdrop-blur-2xl border-t border-white/10"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-2">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-[15px] font-semibold ${
                    isActive ? "bg-white/10 text-white" : "text-white/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/demo"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-demo-btn"
              className="mt-2 inline-flex items-center justify-center px-4 py-3 rounded-md bg-brand-orange text-white font-semibold"
            >
              Book demo →
            </Link>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
